/** @jsxImportSource @emotion/react */
import { ClassNames } from "@emotion/react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import { Alert as MuiAlert } from "@mui/material";
import { forwardRef, ReactNode, JSX } from "react";

import { StylableProps } from "../../types";
import { Typography } from "../typography";

export interface AlertProps extends StylableProps {
  severity: "success" | "info" | "error" | "warning";
  title?: ReactNode;
  message: ReactNode;
  displayTitleAndMessageOnOneLine?: boolean;
  variant?: "filled" | "outlined";
  messageTypographyVariant?: "body" | "footnote";
  onClose?: () => void;
  customIcon?: ReactNode;
  alignIcon?: "top" | "center";
}

/**
 * Displays an alert message to the user
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      title,
      severity,
      message,
      variant,
      onClose,
      customIcon,
      messageTypographyVariant = "body",
      displayTitleAndMessageOnOneLine = false,
      alignIcon = "center",
    }: AlertProps,
    ref
  ): JSX.Element => {
    return (
      <ClassNames>
        {({ css, cx, theme }) => (
          <MuiAlert
            ref={ref}
            variant={variant}
            onClose={onClose}
            slotProps={{
              closeButton: {
                // tighter than MUI's default 5px close button padding
                className: css`
                  padding: 3px;
                `,
              },
            }}
            icon={
              customIcon ? (
                customIcon
              ) : (
                <>
                  {severity === "success" && (
                    <CheckCircleIcon fontSize="inherit" />
                  )}
                  {severity === "info" && (
                    <InfoOutlinedIcon
                      className={css`
                        ${
                          !variant &&
                          css`
                            color: #616161;
                          `
                        }
                      `}
                      fontSize="inherit"
                    />
                  )}
                  {severity === "error" && (
                    <ErrorOutlineIcon fontSize="inherit" />
                  )}
                  {severity === "warning" && (
                    <ReportProblemOutlinedIcon fontSize="inherit" />
                  )}
                </>
              )
            }
            classes={{
              icon: css`
                display: flex;
                align-items: center;
                ${
                  alignIcon === "top" &&
                  css`
                    align-self: flex-start;
                  `
                }
              `,
              // centre the close button vertically (MUI aligns it to the top)
              action: css`
                align-items: center;
                padding-top: 0;
              `,
              message: css`
                ${
                  displayTitleAndMessageOnOneLine &&
                  css`
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: ${theme.spacing(1)};
                  `
                }
              `,
            }}
            className={cx(
              css`
                text-align: left;
                ${
                  severity === "info" &&
                  !variant &&
                  css`
                    background-color: #f5f9fa;
                  `
                }
              `,
              className
            )}
            severity={severity}
          >
            {title && (
              <Typography
                variant="subtitle"
                color="textPrimary"
                css={
                  !displayTitleAndMessageOnOneLine &&
                  css`
                    margin-top: -4px;
                  `
                }
              >
                {title}
              </Typography>
            )}
            <Typography
              variant={messageTypographyVariant}
              color={title && severity === "info" ? "grey.800" : undefined}
            >
              {message}
            </Typography>
          </MuiAlert>
        )}
      </ClassNames>
    );
  }
);
